/**
 * Internal Logger Service
 * Captures errors, warnings, and critical events for debugging
 * Stores logs in localStorage + memory for performance
 */

export type LogLevel = 'error' | 'warn' | 'info' | 'debug' | 'trace';
export type LogCategory = 'auth' | 'db' | 'render' | 'api' | 'storage' | 'ui' | 'lifecycle' | 'uncaught_error' | 'unhandled_promise';

export interface LogEntry {
  timestamp: string;
  level: LogLevel;
  category: LogCategory;
  message: string;
  data?: any;
  stackTrace?: string | undefined;
  userId?: string | undefined;
  userEmail?: string | undefined;
  url?: string | undefined;
  sessionId?: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 500;
  private storageKey = 'lemon_logs';
  private maxStorageSize = 1024 * 50; // 50KB max
  private sessionId = this.generateSessionId();

  constructor() {
    if (typeof window !== 'undefined') {
      this.loadLogsFromStorage();
      this.setupGlobalErrorHandlers();
    }
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupGlobalErrorHandlers() {
    // Capture uncaught errors
    window.addEventListener('error', (event) => {
      this.error('uncaught_error', event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.toString(),
      }, 'render');
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('unhandled_promise', event.reason?.toString() || 'Unknown promise rejection', {
        reason: event.reason,
      }, 'render');
    });
  }

  private loadLogsFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.logs = JSON.parse(stored);
        // Clean old logs (older than 24h)
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        this.logs = this.logs.filter(log => log.timestamp > twentyFourHoursAgo);
      }
    } catch (e) {
      console.warn('[Logger] Failed to load logs from storage:', e);
      this.logs = [];
    }
  }

  private saveLogsToStorage() {
    try {
      const logsToStore = this.logs.slice(-this.maxLogs);
      const serialized = JSON.stringify(logsToStore);
      
      // Check size before storing
      if (serialized.length > this.maxStorageSize) {
        // Keep only last 250 logs if too large
        const trimmed = logsToStore.slice(-250);
        localStorage.setItem(this.storageKey, JSON.stringify(trimmed));
      } else {
        localStorage.setItem(this.storageKey, serialized);
      }
    } catch (e) {
      console.warn('[Logger] Failed to save logs to storage:', e);
      // If storage is full, clear oldest logs
      try {
        const logsToStore = this.logs.slice(-100);
        localStorage.setItem(this.storageKey, JSON.stringify(logsToStore));
      } catch (e2) {
        console.error('[Logger] Storage completely full, cannot save logs');
      }
    }
  }

  private createEntry(
    level: LogLevel,
    category: LogCategory,
    message: string,
    data?: any,
    stackTrace?: string
  ): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      stackTrace,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      sessionId: this.sessionId,
    };
  }

  error(category: LogCategory, message: string, data?: any, stackTrace?: string) {
    const entry = this.createEntry('error', category, message, data, stackTrace);
    this.addLog(entry);
    console.error(`[${category}] ${message}`, data);
  }

  warn(category: LogCategory, message: string, data?: any) {
    const entry = this.createEntry('warn', category, message, data);
    this.addLog(entry);
    console.warn(`[${category}] ${message}`, data);
  }

  info(category: LogCategory, message: string, data?: any) {
    const entry = this.createEntry('info', category, message, data);
    this.addLog(entry);
    console.log(`[${category}] ${message}`, data);
  }

  debug(category: LogCategory, message: string, data?: any) {
    const entry = this.createEntry('debug', category, message, data);
    this.addLog(entry);
    if (process.env['NODE_ENV'] === 'development') {
      console.debug(`[${category}] ${message}`, data);
    }
  }

  trace(category: LogCategory, message: string, data?: any) {
    const entry = this.createEntry('trace', category, message, data);
    this.addLog(entry);
  }

  // Supabase-specific logging
  logSupabaseQuery(table: string, operation: string, success: boolean, duration: number, error?: any) {
    const category: LogCategory = 'db';
    const message = `${operation} on ${table} (${duration}ms)`;
    
    if (success) {
      this.info(category, message, { table, operation, duration });
    } else {
      this.error(category, message, { table, operation, duration, error });
    }
  }

  // Auth-specific logging
  logAuthEvent(event: string, userId?: string, email?: string, data?: any) {
    const entry = this.createEntry('info', 'auth', event, data);
    entry.userId = userId;
    entry.userEmail = email;
    this.addLog(entry);
  }

  // Render error with React component context
  logRenderError(componentName: string, error: Error, errorInfo?: any) {
    this.error('render', `${componentName} render error`, {
      componentName,
      message: error.message,
      errorInfo,
    }, error.stack);
  }

  private addLog(entry: LogEntry) {
    this.logs.push(entry);
    
    // Keep memory bounded
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
    
    // Save to storage
    this.saveLogsToStorage();
  }

  getLogs(filter?: { level?: LogLevel | undefined; category?: LogCategory | undefined; limit?: number | undefined }): LogEntry[] {
    let filtered = this.logs;

    if (filter?.level) {
      filtered = filtered.filter(log => log.level === filter.level);
    }

    if (filter?.category) {
      filtered = filtered.filter(log => log.category === filter.category);
    }

    if (filter?.limit) {
      filtered = filtered.slice(-filter.limit);
    }

    return filtered;
  }

  getErrorSummary() {
    const errors = this.logs.filter(log => log.level === 'error');
    return {
      totalErrors: errors.length,
      byCategory: errors.reduce((acc, log) => {
        acc[log.category] = (acc[log.category] || 0) + 1;
        return acc;
      }, {} as Record<LogCategory, number>),
      recentErrors: errors.slice(-10),
    };
  }

  clearLogs() {
    this.logs = [];
    try {
      localStorage.removeItem(this.storageKey);
    } catch (e) {
      console.warn('[Logger] Failed to clear localStorage');
    }
  }

  exportLogs(): string {
    return JSON.stringify({
      sessionId: this.sessionId,
      exportedAt: new Date().toISOString(),
      logs: this.logs,
      summary: this.getErrorSummary(),
    }, null, 2);
  }

  downloadLogs() {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lemon-logs-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Singleton instance
export const logger = new Logger();

export default logger;
